import { produce } from 'immer';
import _ from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import Button from '../../../../../../common/widgets/Button';
import Icons from '../../../../../../common/widgets/Icons';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { EmployeeTemplateOptionModel } from '../../../../models/EmployeeTemplateOptionModel';
import { EmployeesCustomTemplateOptionsModel } from '../../../../models/EmployeesCustomTemplateOptionsModel';
import { resources } from '../../../../resources/resources';
import { fileImportService } from '../../../../services/file-import';
import InfoPanel from './Components/InfoPanel';
import OptionsTemplate from './Components/OptionsTemplate';
import AddOptionsDialog from './Dialogs/AddOptionsDialog';
import { getNotSetOptions, getSetOptions } from './helpers';

interface EditTemplateState {
  isLoading: boolean;
  isSaving: boolean;
  showAddOptionDialog: boolean;
  options: EmployeeTemplateOptionModel[];
  modifiedOptions: EmployeeTemplateOptionModel[];
}

export default function EditTemplatePage() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<EditTemplateState>(
    {} as EditTemplateState,
  );

  const loadData = async () => {
    setState(
      produce(d => {
        d.isLoading = true;
      }),
    );
    const response = await fileImportService.getEmployeeTemplateOptions();
    if (!response.hasErrors) {
      setState(
        produce(d => {
          d.options = [...response.data];
          d.modifiedOptions = [...response.data];
        }),
      );
    } else {
      showErrorToast(translate(resources.admin.fileImport.loadOptionsError));
    }
  };

  const saveOptionNewName = useCallback(
    (optionId: number, optionName: string) => {
      setState(
        produce(d => {
          d.modifiedOptions = d.modifiedOptions.map(o => {
            if (o.optionId === optionId) {
              o.name = optionName;
            }
            return o;
          });
        }),
      );
    },
    [],
  );

  const addOption = (option: EmployeeTemplateOptionModel) => {
    setState(
      produce(d => {
        d.modifiedOptions = d.modifiedOptions.map(o => {
          if (o.optionId == option.optionId) {
            o.itSet = true;
            o.order = d.modifiedOptions.length + 1;
          }
          return o;
        });
      }),
    );
  };

  const removeOption = useCallback((option: EmployeeTemplateOptionModel) => {
    setState(
      produce(d => {
        d.modifiedOptions = d.modifiedOptions.map(o => {
          if (o.optionId === option.optionId) {
            o.itSet = false;
          }
          return o;
        });
      }),
    );
  }, []);

  const handleSubmit = async () => {
    setState(
      produce(d => {
        d.isSaving = true;
      }),
    );
    const data = state.modifiedOptions
      .filter(o => o.itSet)
      .map(
        (
          o: EmployeeTemplateOptionModel,
        ): EmployeesCustomTemplateOptionsModel => {
          return {
            optionId: o.optionId,
            newName: o.name != o.defaultName ? o.name : '',
            optionOrder: o.order,
          };
        },
      );
    const response = await fileImportService.saveEmployeeTemplate(data);
    if (!response.hasErrors) {
      showSuccessToast(
        translate(resources.admin.fileImport.saveTemplateSuccess),
      );
    } else {
      showErrorToast(translate(resources.admin.fileImport.saveTemplateError));
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };

  const optionChanged = (options: EmployeeTemplateOptionModel[]) => {
    const newOptions = _.concat(
      options,
      getNotSetOptions(state.modifiedOptions),
    );
    setState(
      produce(d => {
        d.modifiedOptions = [...newOptions];
      }),
    );
  };

  useMount(loadData);

  return (
    <Loader loading={!state.modifiedOptions} loader={<LoadingSpinner />}>
      <div className="panel panel-xl templates-panel">
        <div className="btn-panel-back-wrapper">
          <button
            className="btn btn-panel-back"
            onClick={() => navigate('/admin/employee-management/import-files')}
          >
            <span className="btn-text">
              {translate(resources.admin.fileImport.title)}
            </span>
          </button>
        </div>
        <div className="page-actions">
          <h1>{translate(resources.admin.fileImport.editTemplate.title)}</h1>
          <div className="page-action">
            <Button
              className="btn btn-outline btn-sm"
              onClick={() => {
                setState(
                  produce(d => {
                    d.modifiedOptions = [...state.options];
                  }),
                );
              }}
              icon={<Icons.Subtract />}
            >
              <span className="btn-text">
                {translate(
                  resources.admin.fileImport.editTemplate.actions.resetTemplate,
                )}
              </span>
            </Button>
          </div>
        </div>
        <div className="row">
          <div className="col col-8 col-sm-7 col-xs-12">
            <strong>
              {translate(
                resources.admin.fileImport.editTemplate.mandatoryColumns,
              )}
            </strong>
            <Button
              className="btn btn-outline btn-new-column"
              onClick={() => {
                setState(
                  produce(d => {
                    d.showAddOptionDialog = true;
                  }),
                );
              }}
              icon={<Icons.AddButtonAlt />}
            >
              <span className="btn-text">
                {translate(
                  resources.admin.fileImport.editTemplate.actions.addColumn,
                )}
              </span>
            </Button>
            <OptionsTemplate
              options={getSetOptions(state.modifiedOptions ?? [])}
              saveOptionNewName={saveOptionNewName}
              removeOption={removeOption}
              optionsChanged={optionChanged}
            />
          </div>
          <div className="col col-4 col-sm-5 col-xs-12 templates-info-panel">
            <InfoPanel />
          </div>
        </div>
        <div className="form-actions left-actions">
          <Button
            className="btn btn-solid"
            loading={state.isSaving}
            onClick={handleSubmit}
          >
            <span className="btn-text">
              {translate(resources.admin.fileImport.actions.save)}
            </span>
          </Button>
          <Button className="btn btn-outline" onClick={() => navigate(-1)}>
            <span className="btn-text">
              {translate(resources.admin.fileImport.actions.close)}
            </span>
          </Button>
        </div>
      </div>

      {state.showAddOptionDialog && (
        <AddOptionsDialog
          options={getNotSetOptions(state.modifiedOptions ?? [])}
          addOption={addOption}
          close={() =>
            setState(
              produce(d => {
                d.showAddOptionDialog = false;
              }),
            )
          }
        ></AddOptionsDialog>
      )}
    </Loader>
  );
}
